import { atomWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import { R as GeneralResources } from '@/Resources.resx';
import { R } from '@/features/programs/resources/ProgramsResources.resx';

import {
    UpdateSundvaegtProgram,
    SundVaegtProgramDto,
} from '@/shared/interfaces/api/sundvaegt-interfaces';
import { updateSundVaegtProgram } from '../api/updateSundVaegtProgram';
import { fetchSundVaegtProgramQueryAtom } from './fetchSundVaegtProgramQueryAtom';

const queryKey = ['sundvaegt-weightprogram'] as const;

export const updateSundVaegtProgramMutationAtom = atomWithMutation<
    string, // TData (return type)
    Omit<UpdateSundvaegtProgram, 'Id'>, // TVariables (you pass DesiredWeightKg only)
    Error, // TError
    { previous?: SundVaegtProgramDto } // TContext (used for rollback)
>((get) => {
    const queryClient = get(queryClientAtom);

    return {
        mutationFn: async (variables: Omit<UpdateSundvaegtProgram, 'Id'>) => {
            // read the freshest program right before calling the API
            const program = get(fetchSundVaegtProgramQueryAtom).data;
            if (!program) {
                throw new Error('No SundVægt program loaded');
            }
            try {
                return await updateSundVaegtProgram({
                    Id: program.Id,
                    ...variables,
                });
            } catch (error) {
                throw new Error(
                    `${R.SundvaegtSetWeightGoalErrorMessage}: ${
                        error instanceof Error
                            ? error.message
                            : GeneralResources.UnknownError
                    }`,
                );
            }
        },

        // optimistic update
        onMutate: async (variables: Omit<UpdateSundvaegtProgram, 'Id'>) => {
            await queryClient.cancelQueries({ queryKey });

            const previous =
                queryClient.getQueryData<SundVaegtProgramDto>(queryKey);

            if (previous) {
                queryClient.setQueryData<SundVaegtProgramDto>(queryKey, {
                    ...previous,
                    DesiredWeightKg:
                        variables.DesiredWeightKg ?? previous.DesiredWeightKg,
                });
            }

            return { previous };
        },

        // rollback on error
        onError: (_err, _vars, ctx) => {
            if (ctx?.previous) {
                queryClient.setQueryData<SundVaegtProgramDto>(
                    queryKey,
                    ctx.previous,
                );
            }
        },

        // ensure server truth after success
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey });
        },
    };
});
